import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import GlassCard from '../components/GlassCard';

const PrivacyPolicyPage = () => {
  const { theme } = useTheme();

  return (
    <div style={{ padding: '32px 0', maxWidth: '900px', margin: '0 auto' }}>
      <Link
        to="/"
        style={{
          color: theme.text.secondary,
          textDecoration: 'none',
          marginBottom: '24px',
          display: 'inline-block',
          fontSize: '14px',
        }}
      >
        ← Back to Home
      </Link>

      <GlassCard>
        <h1 style={{ fontSize: '36px', marginBottom: '8px', color: theme.text.primary }}>
          Privacy Policy – Family Tree
        </h1>
        <p style={{ color: theme.text.muted, fontSize: '14px', marginBottom: '32px' }}>
          Last updated: January 5, 2026
        </p>

        <div style={{ color: theme.text.secondary, lineHeight: '1.8', fontSize: '16px' }}>
          <p style={{ marginBottom: '24px' }}>
            Family Tree ("we," "our," or "the app") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect your information when you use the Family Tree mobile application and related services.
          </p>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              1. Information We Collect
            </h2>

            <h3 style={{ fontSize: '20px', marginBottom: '12px', marginTop: '20px', color: theme.text.primary }}>
              A. Information You Provide
            </h3>
            <p style={{ marginBottom: '16px' }}>
              When you use Family Tree, you may voluntarily provide information including:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Full name</li>
              <li>Date of birth</li>
              <li>Place of birth or residence</li>
              <li>Family relationships</li>
              <li>Hobbies and interests</li>
              <li>Social media links</li>
              <li>Photos (if enabled)</li>
              <li>Any notes or descriptions you choose to add</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              You may also add information about other family members, and you confirm that you have permission to share this information.
            </p>

            <h3 style={{ fontSize: '20px', marginBottom: '12px', marginTop: '20px', color: theme.text.primary }}>
              B. Authentication Information
            </h3>
            <p style={{ marginBottom: '16px' }}>
              We use Google Authentication to securely sign you in. Through Google, we may receive:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Your name</li>
              <li>Email address</li>
              <li>Profile picture (if available)</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              We do not access, store, or see your Google password.
            </p>

            <h3 style={{ fontSize: '20px', marginBottom: '12px', marginTop: '20px', color: theme.text.primary }}>
              C. Automatically Collected Information
            </h3>
            <p style={{ marginBottom: '16px' }}>
              We may collect limited technical information such as:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Device type and operating system</li>
              <li>App usage data (for performance and improvement)</li>
              <li>Crash logs or error reports</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              2. How We Use Your Information
            </h2>
            <p style={{ marginBottom: '16px' }}>
              We use your information to:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Create and manage your family tree</li>
              <li>Enable collaboration between family members</li>
              <li>Authenticate users securely</li>
              <li>Improve app functionality and user experience</li>
              <li>Maintain security and prevent misuse</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              We do not sell, rent, or trade your personal data.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              3. Sharing of Information
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Your information is shared only:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>With family members you choose to connect with</li>
              <li>With trusted service providers necessary to operate the app (e.g., authentication or hosting)</li>
              <li>When required by law or legal process</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              We do not share your data for advertising or marketing purposes.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              4. Data Visibility & User Control
            </h2>
            <p style={{ marginBottom: '16px' }}>
              You have full control over your data:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>You decide what information to add</li>
              <li>You choose which family members can view your data</li>
              <li>You can edit or remove information at any time</li>
              <li>You can request permanent account deletion</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              5. Data Storage & Security
            </h2>
            <p style={{ marginBottom: '16px' }}>
              We use industry-standard security practices, including:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Secure authentication</li>
              <li>Encrypted connections</li>
              <li>Restricted internal access to data</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              While we take strong precautions, no system can be guaranteed to be completely secure.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              6. Children's Privacy
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Family Tree may include information about children only when added by a parent or legal guardian. We do not knowingly collect personal data from children without appropriate consent.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              7. Account Deletion
            </h2>
            <p style={{ marginBottom: '16px' }}>
              You may request account deletion at any time by:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Using the in-app delete option (if available), or</li>
              <li>Contacting us at{' '}
                <a
                  href="mailto:twodotsai@gmail.com"
                  style={{ color: theme.accent.primary, textDecoration: 'none' }}
                >
                  twodotsai@gmail.com
                </a>
              </li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              Once deleted:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Your personal data will be permanently removed</li>
              <li>Shared family data may be removed or anonymized depending on family permissions</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              8. Third-Party Services
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Family Tree uses third-party services such as:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Google Authentication</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              These services operate under their own privacy policies.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              9. Changes to This Policy
            </h2>
            <p style={{ marginBottom: '16px' }}>
              We may update this Privacy Policy from time to time. Updates will be reflected by changing the "Last updated" date. Significant changes will be communicated to users when appropriate.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              10. Contact Us
            </h2>
            <p style={{ marginBottom: '16px' }}>
              If you have questions or concerns about this Privacy Policy, please contact us:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px', listStyle: 'none' }}>
              <li style={{ marginBottom: '8px' }}>
                📧 Email:{' '}
                <a
                  href="mailto:twodotsai@gmail.com"
                  style={{ color: theme.accent.primary, textDecoration: 'none' }}
                >
                  twodotsai@gmail.com
                </a>
              </li>
              <li>
                🌐 Website:{' '}
                <a
                  href="https://ourfamilytree.cloud"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: theme.accent.primary, textDecoration: 'none' }}
                >
                  https://ourfamilytree.cloud
                </a>
              </li>
            </ul>
          </section>
        </div>
      </GlassCard>
    </div>
  );
};

export default PrivacyPolicyPage;






